import { useState, useEffect } from "react";
import {
  Flag, Target, Heart, Users, TrendingUp, Award, Handshake, Megaphone,
  ShieldCheck, Check, Calendar, MapPin, Trophy, Sparkles, Building2, Star,
  Mail, Phone, Globe, Quote, ArrowRight, Landmark, GraduationCap, BarChart3, Download,
} from "lucide-react";
import { CONTACT, SPONSOR_DEADLINES, SPONSOR_CAPACITY_DEFAULTS, ACTIVATION_DEFAULTS, ACTIVATION_INCLUDES, IN_KIND_NOTE, SCHEDULE, MATRIX_COLUMNS, BENEFITS_MATRIX, FOUNDING_PARTNER } from "@/data/content";

/* Executive Sponsorship Prospectus — print-ready multi-page (Letter). Route: /prospectus */
const NAVY = "#0e1e3a";
const NAVY2 = "#16294a";
const CHARCOAL = "#2b2f36";
const RED = "#b42230";
const GOLD = "#c9a24b";
const PAPER = "#ffffff";
const MIST = "#f4f6f9";
const INK = "#1c2230";

const TIERS = [
  { name: "Legacy", tag: "Presenting Sponsor", price: 30000, avail: "1 Available", color: RED },
  { name: "Impact", tag: "Premier Partner", price: 15000, avail: "3 Available", color: NAVY },
  { name: "Courage", tag: "Signature Partner", price: 10000, avail: "5 Available", color: NAVY },
  { name: "Honor", tag: "Supporting Partner", price: 7500, avail: "8 Available", color: CHARCOAL },
  { name: "Service", tag: "Community Partner", price: 5000, avail: "10 Available", color: CHARCOAL },
];

const MATRIX_TIERS = MATRIX_COLUMNS;
const cell = (c) => (c === true ? "✓" : c === false ? "—" : c);

const SCHEDULE_ROWS = SCHEDULE.map((s) => [s.time, s.title, s.desc]);

const TIMELINE = [
  ["Upon Commitment", "Immediate logo placement on the event website and sponsor recognition begins."],
  ["3–6 Months Out", "Inclusion in email campaigns, social media announcements, and press outreach."],
  ["1 Month Out", "Feature in event program, on-course signage production, and player communications."],
  ["Event Day", "On-site branding, activations, hospitality, and podium recognition."],
  ["Post-Event", "Thank-you features, impact recap, and year-round Founding Partner recognition."],
];

const FAQ = [
  ["What does my sponsorship support?", "Every dollar advances the Wayne Walker Foundation's mission to support firefighters, law enforcement officers, EMS professionals, and emergency dispatchers and their families."],
  ["Is my sponsorship tax-deductible?", "The Foundation will provide documentation for your contribution. Please consult your tax advisor regarding deductibility."],
  ["Can we customize a package?", "Absolutely. We welcome custom activations and exclusive opportunities tailored to your company's goals."],
  ["When is payment due?", "A commitment secures your level and benefits. Our team will coordinate invoicing and payment timing with you directly."],
  ["Can we send our own foursome?", "Yes. Included player spots can be filled by your team, clients, or guests of your choosing."],
  ["What if we can't attend?", "Your brand benefits and community impact continue regardless of attendance, and we can host clients on your behalf."],
];

const FAB = ["Leadership", "Legacy", "Community", "Service", "Partnership"];

export default function Prospectus() {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const c = () => {
      const g = window.innerWidth < 640 ? 16 : 40;
      setScale(Math.min(1, (window.innerWidth - g * 2) / 816));
    };
    c(); window.addEventListener("resize", c); return () => window.removeEventListener("resize", c);
  }, []);

  return (
    <div style={{ background: "#3a3f47" }} className="w-full flex flex-col items-center py-8 px-2 sm:px-0 print:p-0 print:bg-white">
      <style>{`
        @media print {
          @page { size: letter; margin: 0; }
          html, body { margin: 0 !important; background: #fff !important; }
          .no-print { display: none !important; }
          .pscale { width: auto !important; height: auto !important; margin: 0 !important; }
          .pg { box-shadow: none !important; transform: none !important; margin: 0 !important; break-after: page; page-break-after: always; }
          .pg:last-child { break-after: auto; page-break-after: auto; }
        }
        .pg, .pg * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .pg { font-family: 'Helvetica Neue', Arial, sans-serif; }
        .pg h1,.pg h2,.pg h3,.pg .serif { font-family: Georgia, 'Times New Roman', serif; }
      `}</style>

      <a href="/wayne-walker-sponsorship-prospectus.pdf" download data-testid="prospectus-print-btn"
        className="no-print fixed top-4 right-4 z-50 text-xs font-semibold tracking-widest uppercase text-white px-5 py-3 flex items-center gap-2 shadow-lg"
        style={{ background: RED }}>
        <Download size={15} /> Download Prospectus (PDF)
      </a>

      {PAGES.map((P, i) => (
        <div key={i} className="pscale" style={{ width: 816 * scale, height: 1056 * scale, marginBottom: 12 }}>
          <div className="pg" style={{ width: 816, height: 1056, transform: `scale(${scale})`, transformOrigin: "top left", background: PAPER, position: "relative", overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,.4)" }}>
            <P pageNum={i + 1} total={PAGES.length} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- shared building blocks ---------- */
function Footer({ n, total }) {
  return (
    <div style={{ position: "absolute", bottom: 26, left: 56, right: 56, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#9aa3b2" }}>
      <span>Wayne Walker Foundation · Corporate Sponsorship Prospectus</span>
      <span>{String(n).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
    </div>
  );
}
function Kicker({ children }) {
  return <div style={{ color: RED, fontSize: 12, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 700 }}>{children}</div>;
}
function H2({ children, style }) {
  return <h2 className="serif" style={{ color: INK, fontSize: 34, lineHeight: 1.1, fontWeight: 700, margin: "10px 0 0", ...style }}>{children}</h2>;
}
function Body({ children, style }) {
  return <p style={{ color: "#3c4457", fontSize: 13.5, lineHeight: 1.7, margin: "10px 0 0", ...style }}>{children}</p>;
}
function ContentPage({ n, total, kicker, title, children, dark }) {
  return (
    <div style={{ padding: "56px 56px 70px", height: "100%", background: dark ? NAVY : PAPER }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <div style={{ width: 34, height: 3, background: RED }} />
        <Flag size={14} style={{ color: dark ? "#fff" : NAVY }} />
      </div>
      <Kicker>{kicker}</Kicker>
      <H2 style={{ color: dark ? "#fff" : INK }}>{title}</H2>
      <div style={{ marginTop: 18 }}>{children}</div>
      <Footer n={n} total={total} />
    </div>
  );
}
function Stat({ big, label }) {
  return (
    <div style={{ border: "1px solid #e2e6ee", padding: "18px 16px", background: MIST }}>
      <div className="serif" style={{ color: RED, fontSize: 30, fontWeight: 700 }}>{big}</div>
      <div style={{ color: "#57607a", fontSize: 11.5, marginTop: 4 }}>{label}</div>
    </div>
  );
}
function Callout({ icon: Icon, title, children }) {
  return (
    <div style={{ borderLeft: `4px solid ${RED}`, background: MIST, padding: "16px 18px", marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: NAVY, fontWeight: 700, fontSize: 14 }}>
        {Icon && <Icon size={16} style={{ color: RED }} />} {title}
      </div>
      <div style={{ color: "#3c4457", fontSize: 12.5, lineHeight: 1.6, marginTop: 6 }}>{children}</div>
    </div>
  );
}
function FeatureCard({ icon: Icon, title, children }) {
  return (
    <div style={{ border: "1px solid #e2e6ee", padding: "18px", background: "#fff" }}>
      <div style={{ width: 40, height: 40, background: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={18} /></div>
      <div className="serif" style={{ color: INK, fontSize: 16, fontWeight: 700, marginTop: 12 }}>{title}</div>
      <div style={{ color: "#57607a", fontSize: 12, lineHeight: 1.55, marginTop: 6 }}>{children}</div>
    </div>
  );
}

/* ---------- pages ---------- */
const PAGES = [
  // 1. COVER
  ({ pageNum, total }) => (
    <div style={{ height: "100%", background: NAVY, color: "#fff", position: "relative", overflow: "hidden" }}>
      <img src="/flat/cover-navy.jpg" alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      <div style={{ position: "relative", padding: "70px 60px", height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 12, letterSpacing: "0.3em", textTransform: "uppercase", color: GOLD }}>Wayne Walker Foundation</div>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#aeb8ca" }}>EST. 2027</div>
        </div>
        <div style={{ height: 3, width: 70, background: RED, marginTop: 20 }} />
        <div style={{ marginTop: "auto" }}>
          <div style={{ fontSize: 13, letterSpacing: "0.3em", textTransform: "uppercase", color: "#c7d0e0" }}>Corporate Sponsorship Prospectus</div>
          <h1 className="serif" style={{ fontSize: 58, lineHeight: 1.04, fontWeight: 700, marginTop: 14 }}>
            The Heroes & Legacy<br /><span style={{ color: GOLD }}>Golf Classic</span>
          </h1>
          <p style={{ fontSize: 15, color: "#c7d0e0", marginTop: 18, maxWidth: 520, lineHeight: 1.6 }}>
            An invitation to partner in honoring service, strengthening community, and building a lasting legacy for the men and women who answer the call.
          </p>
          <div style={{ marginTop: 26, display: "flex", gap: 22, fontSize: 12.5, color: "#e6ebf3" }}>
            <span><strong style={{ color: GOLD }}>April 8, 2027</strong> · The Woodlands Country Club — The Trails</span>
            <span>Executive Sponsorship Packet</span>
          </div>
        </div>
        <div style={{ marginTop: 40, fontSize: 11, color: "#9aa3b2", letterSpacing: "0.1em" }}>
          {CONTACT.website} · {CONTACT.sponsorEmail} · {CONTACT.sponsorPhone}
        </div>
      </div>
    </div>
  ),

  // 2. LETTER FROM THE FOUNDER
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="A Personal Invitation" title="Letter From the Founder">
      <Body>Dear Friend and Community Partner,</Body>
      <Body>Every day, firefighters, law enforcement officers, EMS professionals, and emergency dispatchers leave their families to protect ours. They run toward danger, carry the weight of impossible moments, and ask for little in return. The Wayne Walker Foundation exists to make sure they are never alone when they need us most.</Body>
      <Body>This tournament is more than a round of golf. It is a movement — a chance for our business community to stand behind the people who stand behind all of us. It carries forward the legacy of Wayne Walker, a man defined by generosity, leadership, and an unwavering commitment to service.</Body>
      <Body>Sponsoring this event is both a meaningful community investment and a genuine business opportunity: visibility among engaged decision-makers, authentic brand alignment with service and integrity, and relationships that endure well beyond a single day.</Body>
      <Body>I invite your organization to join us as a Founding Partner in what we intend to become one of the region's premier annual charitable events. Your leadership today will be remembered for years to come.</Body>
      <Body style={{ marginTop: 22 }}>With gratitude,</Body>
      <div className="serif" style={{ fontSize: 22, color: NAVY, marginTop: 8, fontStyle: "italic" }}>The Wayne Walker Foundation</div>
      <div style={{ fontSize: 12, color: "#57607a" }}>Founder & Board of Directors</div>
      <Callout icon={Quote} title="Our Promise">Every contribution is stewarded with integrity and directed toward meaningful, measurable support for first responders and their families.</Callout>
    </ContentPage>
  ),

  // 3. ABOUT THE FOUNDATION
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Who We Are" title="About the Wayne Walker Foundation">
      <Body>The Wayne Walker Foundation was established on a simple belief: communities are strongest when they invest in the people who dedicate their lives to serving others. Through fundraising events, community partnerships, and charitable initiatives, we recognize and support the first responders who protect our neighborhoods.</Body>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 18 }}>
        <Stat big="4" label="First-responder disciplines supported" />
        <Stat big="100%" label="Mission-driven fund stewardship" />
        <Stat big="2027" label="Inaugural annual Classic" />
      </div>
      <img src="/wayne-walker-tribute.jpg" alt="Wayne Walker tribute" style={{ width: "100%", height: 250, objectFit: "cover", marginTop: 18, border: "1px solid #e2e6ee" }} />
      <Body style={{ fontSize: 11.5, color: "#8892a5", marginTop: 8 }}>A tribute to Wayne Walker — honoring a legacy of service alongside the turnout gear of those who carry it forward.</Body>
    </ContentPage>
  ),

  // 4. MISSION / WHY WE EXIST
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Purpose" title="Our Mission & Why We Exist">
      <Callout icon={Target} title="Our Mission">To honor and support firefighters, law enforcement officers, EMS professionals, and emergency dispatchers — and to inspire our community to lead through generosity and service.</Callout>
      <H2 style={{ fontSize: 20, marginTop: 24 }}>Why We Exist</H2>
      <Body>First responders give everything to keep our community safe — often at great personal cost. When injury strikes, when illness hits home, or when the weight of the job becomes too much to carry alone, they deserve a community that stands behind them.</Body>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
        <FeatureCard icon={Heart} title="Financial Hardship">Immediate assistance for responders and families facing unexpected hardship.</FeatureCard>
        <FeatureCard icon={ShieldCheck} title="Health & Wellness">Cancer support and behavioral-health resources for those who serve.</FeatureCard>
        <FeatureCard icon={GraduationCap} title="Scholarships">Educational opportunities for responders and their dependents.</FeatureCard>
        <FeatureCard icon={Users} title="Community">Programs that strengthen the bond between responders and the public.</FeatureCard>
      </div>
    </ContentPage>
  ),

  // 5. HONORING WAYNE WALKER'S LEGACY
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="The Namesake" title="Honoring Wayne Walker's Legacy" dark>
      <Body style={{ color: "#c7d0e0" }}>Wayne Walker lived a life defined by generosity, leadership, and a deep respect for those who serve. He believed that true success is measured not by what we accumulate, but by what we give back.</Body>
      <Body style={{ color: "#c7d0e0" }}>The Foundation carries his name — and his values — forward. Every sponsorship, every dollar, and every round played is a tribute to a legacy of service that continues to inspire our community.</Body>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 22 }}>
        {FAB.map((f) => (
          <div key={f} style={{ border: "1px solid #435065", background: "#1a2944", padding: "10px 16px", color: "#fff", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase" }}>{f}</div>
        ))}
      </div>
      <div style={{ borderLeft: `4px solid ${GOLD}`, paddingLeft: 18, marginTop: 26 }}>
        <div className="serif" style={{ color: "#fff", fontSize: 22, fontStyle: "italic", lineHeight: 1.4 }}>"Communities are strongest when they invest in the people who dedicate their lives to serving others."</div>
      </div>
    </ContentPage>
  ),

  // 6. SUPPORTING FIRST RESPONDERS
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Who We Serve" title="Supporting Those Who Serve">
      <Body>Your partnership provides meaningful support across every branch of first response — the professionals who protect, rescue, and care for our community around the clock.</Body>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
        <FeatureCard icon={Flag} title="Firefighters">Support for those who face fire, disaster, and rescue on the front lines.</FeatureCard>
        <FeatureCard icon={ShieldCheck} title="Law Enforcement">Standing with the officers who protect and serve our neighborhoods.</FeatureCard>
        <FeatureCard icon={Heart} title="EMS Professionals">Honoring the medics who deliver care in life's most critical moments.</FeatureCard>
        <FeatureCard icon={Megaphone} title="Emergency Dispatchers">Recognizing the calm voices who coordinate every response.</FeatureCard>
      </div>
      <Callout icon={BarChart3} title="Impact Reporting">Funds raised, families assisted, and scholarships awarded are reported to every sponsor following the event — so you can see exactly what your partnership made possible.</Callout>
    </ContentPage>
  ),

  // 7. VISION FOR THE ANNUAL CLASSIC
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="The Road Ahead" title="Vision for the Annual Golf Classic">
      <Body>The Heroes & Legacy Golf Classic is designed from day one to become a signature annual tradition — a premier gathering where business leaders, community members, and first responders come together for a shared purpose.</Body>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 18 }}>
        <Stat big="36" label="Teams · 144 players" />
        <Stat big="$125K" label="Inaugural fundraising goal" />
        <Stat big="Annual" label="Growing tradition" />
      </div>
      <Body style={{ marginTop: 18 }}>As the event grows, so does its reach — and the visibility and impact available to our founding sponsors. Early partners will be recognized as the organizations that helped launch a lasting institution.</Body>
      <img src="/ff-flag-raise.jpg" alt="" style={{ width: "100%", height: 220, objectFit: "cover", marginTop: 16, border: "1px solid #e2e6ee" }} />
    </ContentPage>
  ),

  // 8. WHY BUSINESSES SPONSOR
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="The Case for Partnership" title="Why Businesses Sponsor Community Events">
      <Body>Today's customers, employees, and partners expect the companies they support to stand for something. Sponsorship of a respected charitable event delivers on that expectation — while producing tangible business returns.</Body>
      <div style={{ marginTop: 16 }}>
        {[
          ["Purpose-driven brands outperform", "Consumers consistently prefer and stay loyal to companies that demonstrate authentic community commitment."],
          ["Trust by association", "Aligning with first responders and a respected foundation transfers goodwill and credibility to your brand."],
          ["High-value audience", "Engage executives, business owners, and community leaders in a relaxed, relationship-rich setting."],
          ["Employee pride", "Give your team a meaningful way to represent your company and give back together."],
        ].map(([t, d]) => (
          <div key={t} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: "1px solid #eef1f6" }}>
            <Check size={18} style={{ color: RED, flexShrink: 0, marginTop: 2 }} />
            <div><div style={{ fontWeight: 700, color: INK, fontSize: 13.5 }}>{t}</div><div style={{ color: "#57607a", fontSize: 12.5, marginTop: 2 }}>{d}</div></div>
          </div>
        ))}
      </div>
    </ContentPage>
  ),

  // 9. BUSINESS VALUE OF SPONSORSHIP (5 pillars)
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Return & Reach" title="The Business Value of Sponsorship">
      <Body>Sponsorship of the Heroes & Legacy Golf Classic delivers value across five dimensions — a balanced return of impact, exposure, and opportunity.</Body>
      <div style={{ marginTop: 16 }}>
        {[
          [Heart, "Community Impact", "Directly fund programs that support first responders and strengthen the communities they serve."],
          [Megaphone, "Brand Visibility", "Year-round recognition across web, social, print, signage, and on-site branding."],
          [Handshake, "Relationship Building", "Meaningful connections with leaders, partners, customers, and future clients."],
          [Users, "Employee Engagement", "A premium team experience that builds pride and culture."],
          [Landmark, "Lasting Legacy", "A permanent place in an annual tradition dedicated to service."],
        ].map(([Ic, t, d], i) => (
          <div key={t} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 0", borderBottom: "1px solid #eef1f6" }}>
            <div style={{ fontFamily: "Georgia, serif", fontSize: 24, color: "#d6dbe6", fontWeight: 700, width: 34 }}>{String(i + 1).padStart(2, "0")}</div>
            <div style={{ width: 38, height: 38, background: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Ic size={17} /></div>
            <div><div style={{ fontWeight: 700, color: INK, fontSize: 14 }}>{t}</div><div style={{ color: "#57607a", fontSize: 12.3 }}>{d}</div></div>
          </div>
        ))}
      </div>
    </ContentPage>
  ),

  // 10. COMMUNITY IMPACT + BRAND VISIBILITY
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Value In Depth" title="Community Impact & Brand Visibility">
      <H2 style={{ fontSize: 19 }}>Community Impact</H2>
      <Body>Your sponsorship translates directly into support for the people who protect us — funding assistance, wellness resources, and scholarships. It is a visible, values-aligned statement of your company's commitment to the community it serves.</Body>
      <H2 style={{ fontSize: 19, marginTop: 22 }}>Brand Visibility</H2>
      <Body>From the moment you commit, your brand is featured across the event's marketing ecosystem — website, email, social media, printed program, on-course signage, and awards reception — reaching an engaged, affluent, community-minded audience.</Body>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 18 }}>
        <Stat big="144" label="Engaged players on-site" />
        <Stat big="36" label="Teams in the field" />
        <Stat big="18" label="Branded holes on the course" />
      </div>
    </ContentPage>
  ),

  // 11. EMPLOYEE ENGAGEMENT + RELATIONSHIP BUILDING
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="People & Relationships" title="Employee Engagement & Relationship Building">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <FeatureCard icon={Sparkles} title="Inspire Your Team">Reward employees with a premium event experience and give them a source of pride in your company's community leadership.</FeatureCard>
        <FeatureCard icon={Handshake} title="Build Relationships">Host clients, connect with community leaders, and cultivate authentic, purpose-driven relationships that endure.</FeatureCard>
      </div>
      <Callout icon={Users} title="A Shared Day of Purpose">Sponsors, employees, clients, and the first responders we honor share the course, the reception, and the recognition — relationships built shoulder to shoulder, not across a table.</Callout>
    </ContentPage>
  ),

  // 12. HOSPITALITY + TOURNAMENT OVERVIEW
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="The Experience" title="Hospitality & Tournament Overview">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div><div style={{ display: "flex", gap: 8, alignItems: "center", color: NAVY, fontWeight: 700 }}><Calendar size={15} style={{ color: RED }} /> Date</div><Body style={{ margin: "4px 0 0" }}>Thursday, April 8, 2027</Body></div>
        <div><div style={{ display: "flex", gap: 8, alignItems: "center", color: NAVY, fontWeight: 700 }}><MapPin size={15} style={{ color: RED }} /> Venue</div><Body style={{ margin: "4px 0 0" }}>The Woodlands Country Club — The Trails · 2311 N Millbend Dr, Spring, TX 77380</Body></div>
        <div><div style={{ display: "flex", gap: 8, alignItems: "center", color: NAVY, fontWeight: 700 }}><Trophy size={15} style={{ color: RED }} /> Format</div><Body style={{ margin: "4px 0 0" }}>Four-person scramble · Shotgun start</Body></div>
        <div><div style={{ display: "flex", gap: 8, alignItems: "center", color: NAVY, fontWeight: 700 }}><Users size={15} style={{ color: RED }} /> Field</div><Body style={{ margin: "4px 0 0" }}>36 teams · 144 players</Body></div>
      </div>
      <Body>Sponsors enjoy elevated hospitality throughout the day — from VIP registration and on-course refreshments to the awards reception. Premium levels include dedicated activation space and prominent recognition, creating natural, relaxed opportunities to connect with guests and community leaders.</Body>
      <Callout icon={Award} title="Hospitality Highlights">Breakfast · on-course food & beverage · player gift bags · awards reception & lunch · first-responder tribute.</Callout>
    </ContentPage>
  ),

  // 13. EVENT SCHEDULE
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Day Of" title="Event Schedule">
      <div style={{ marginTop: 8 }}>
        {SCHEDULE_ROWS.map(([t, title, d]) => (
          <div key={t} style={{ display: "flex", gap: 18, padding: "16px 0", borderBottom: "1px solid #eef1f6", alignItems: "flex-start" }}>
            <div style={{ width: 92, flexShrink: 0 }}>
              <div style={{ color: RED, fontWeight: 700, fontSize: 15, fontFamily: "Georgia, serif" }}>{t}</div>
            </div>
            <div><div style={{ fontWeight: 700, color: INK, fontSize: 15 }}>{title}</div><div style={{ color: "#57607a", fontSize: 12.5, marginTop: 2 }}>{d}</div></div>
          </div>
        ))}
      </div>
      <Callout icon={Sparkles} title="Sponsor Access">Sponsors are welcomed early for VIP check-in and remain honored guests through the reception and awards presentation.</Callout>
    </ContentPage>
  ),

  // 14. SPONSORSHIP PHILOSOPHY
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Our Approach" title="Sponsorship Philosophy" dark>
      <Body style={{ color: "#c7d0e0" }}>We believe sponsorship should be a true partnership — mutually valuable, transparent, and built on shared purpose. Each level is designed to deliver a meaningful return of impact, exposure, and opportunity that reflects your organization's commitment.</Body>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 20 }}>
        {[
          [Target, "Meaningful", "Every dollar advances a clear mission with real community impact."],
          [TrendingUp, "Valuable", "Tangible brand exposure and business relationships in return."],
          [Handshake, "Flexible", "Custom activations tailored to your goals are always welcome."],
          [Award, "Lasting", "Founding sponsors are recognized for years to come."],
        ].map(([Ic, t, d]) => (
          <div key={t} style={{ border: "1px solid #3e4b61", background: "#182742", padding: 16 }}>
            <Ic size={20} style={{ color: GOLD }} />
            <div className="serif" style={{ color: "#fff", fontSize: 16, fontWeight: 700, marginTop: 10 }}>{t}</div>
            <div style={{ color: "#aeb8ca", fontSize: 12, marginTop: 4 }}>{d}</div>
          </div>
        ))}
      </div>
    </ContentPage>
  ),

  // 15. BENEFITS MATRIX
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="At a Glance" title="Sponsorship Benefits Matrix">
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12, fontSize: 9.5 }}>
        <thead>
          <tr style={{ background: NAVY, color: "#fff" }}>
            <th style={{ textAlign: "left", padding: "8px 8px", fontSize: 9.5 }}>Benefit</th>
            {MATRIX_TIERS.map((t) => <th key={t.key} style={{ padding: "8px 3px", fontSize: 9 }}>{t.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {BENEFITS_MATRIX.map((row, i) => (
            <tr key={row.label} style={{ background: i % 2 ? MIST : "#fff" }}>
              <td style={{ padding: "7px 8px", color: INK, borderBottom: "1px solid #eef1f6", fontWeight: 600 }}>{row.label}</td>
              {row.values.map((v, j) => {
                const c = cell(v);
                return (
                  <td key={`${row.label}-${MATRIX_TIERS[j].key}`} style={{ textAlign: "center", padding: "7px 3px", borderBottom: "1px solid #eef1f6", color: c === "✓" ? RED : c === "—" ? "#c2c8d2" : "#3c4457", fontWeight: c === "✓" ? 700 : 500, lineHeight: 1.25 }}>{c}</td>
                );
              })}
            </tr>
          ))}
          <tr style={{ background: NAVY, color: "#fff" }}>
            <td style={{ padding: "9px 8px", fontWeight: 700 }}>Investment</td>
            {MATRIX_TIERS.map((t) => <td key={t.key} style={{ textAlign: "center", padding: "9px 3px", fontWeight: 700, color: GOLD }}>${t.price.toLocaleString()}</td>)}
          </tr>
        </tbody>
      </table>
      <Body style={{ fontSize: 10.5, color: "#8892a5", marginTop: 8 }}>Founding Partner recognition applies to every level committed by {SPONSOR_DEADLINES.foundingDate}. Custom packages available on request.</Body>
    </ContentPage>
  ),

  // 16. TIER DETAIL — Legacy & Leadership
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Sponsorship Levels" title="Legacy & Impact">
      {[TIERS[0], TIERS[1]].map((t) => (
        <div key={t.name} style={{ border: "1px solid #e2e6ee", borderTop: `4px solid ${t.color}`, padding: 18, marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div><div className="serif" style={{ fontSize: 24, fontWeight: 700, color: INK }}>{t.name} Sponsor</div><div style={{ color: RED, fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase" }}>{t.tag} · {t.avail}</div></div>
            <div className="serif" style={{ fontSize: 30, fontWeight: 700, color: t.color }}>${t.price.toLocaleString()}</div>
          </div>
          <Body style={{ marginTop: 8, fontSize: 12.5 }}>
            {t.name === "Legacy"
              ? "Our most prestigious, exclusive partnership. Presenting naming rights, two complimentary foursomes (8 golfers), premium hole sponsorship, welcome remarks at the opening ceremony, premier logo placement across signage, website, marketing and social media, full-page program recognition, booth/display space, gift-bag promotional items, VIP hospitality with reserved seating, awards-ceremony recognition, and first right of renewal."
              : "A premier partnership with one complimentary foursome (4 golfers), prominent logo placement on signage, premium recognition on the website and social media, half-page program recognition, premium hole sponsorship, booth/display opportunity, gift-bag promotional items, recognition at the opening and closing ceremonies, and a priority renewal invitation."}
          </Body>
        </div>
      ))}
      <Callout icon={Star} title="Presenting Exclusivity">The Legacy level is limited to a single presenting sponsor — the highest visibility and association available.</Callout>
    </ContentPage>
  ),

  // 17. TIER DETAIL — Courage, Honor, Service, Tribute
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Sponsorship Levels" title="Courage, Honor, Service & Hero Team">
      {[TIERS[2], TIERS[3], TIERS[4]].map((t) => {
        const featured = t.name === "Courage";
        return (
        <div key={t.name} style={{ border: featured ? `2px solid ${RED}` : "1px solid #e2e6ee", borderLeft: `4px solid ${featured ? RED : t.color}`, padding: 12, marginTop: 8, background: featured ? "#fcf6f7" : "#fff", position: "relative" }}>
          {featured && <span style={{ position: "absolute", top: -9, left: 12, background: RED, color: "#fff", fontSize: 8.5, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "2px 8px" }}>Most Popular</span>}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div><div className="serif" style={{ fontSize: 18, fontWeight: 700, color: INK }}>{t.name} Sponsor</div><div style={{ color: RED, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase" }}>{t.tag} · {t.avail}</div></div>
            <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: featured ? RED : t.color }}>${t.price.toLocaleString()}</div>
          </div>
          <Body style={{ marginTop: 4, fontSize: 11.5 }}>
            {t.name === "Courage" && "One complimentary foursome (4 golfers), premium hole sponsorship, logo on the sponsor recognition board and event website, quarter-page program recognition, social media recognition, gift-bag promotional items, and recognition during the awards ceremony — the strongest balance of visibility and value."}
            {t.name === "Honor" && "Two golfer registrations, hole sponsorship with custom signage, logo on the sponsor recognition board, website listing, program recognition, social media recognition, and gift-bag promotional items."}
            {t.name === "Service" && "Hole sponsorship with custom signage, company name on the sponsor recognition board, program recognition, website listing, and post-event social media recognition — an accessible way to support the mission and be named a Founding Partner."}
          </Body>
        </div>
        );
      })}
      {/* Sponsor a Hero Team — mission-based level */}
      <div style={{ border: "1px solid #e2e6ee", borderLeft: `4px solid ${NAVY}`, padding: 12, marginTop: 8, background: "#fff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div><div className="serif" style={{ fontSize: 18, fontWeight: 700, color: INK }}>Sponsor a Hero Team</div><div style={{ color: RED, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase" }}>Sends one four-person first responder team to the course · {SPONSOR_CAPACITY_DEFAULTS.tribute} Available</div></div>
          <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: NAVY }}>$2,500</div>
        </div>
        <Body style={{ marginTop: 4, fontSize: 11.5 }}>
          Underwrites the full tournament experience for a team of local first responders — registration, carts, meals, gift bags — with Hero Team Sponsor recognition on the team cart, website, program, sponsor board, and at the ceremony. Includes a professional team photo op with your sponsored heroes, yours to share.
        </Body>
      </div>
      <Callout icon={Handshake} title="Every Level Matters">All sponsors who commit by {SPONSOR_DEADLINES.foundingDate} — at every level — are recognized permanently as inaugural Founding Partners of the Classic.</Callout>
    </ContentPage>
  ),

  // 18. EXCLUSIVE + BRAND RECOGNITION OPPORTUNITIES
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="À La Carte" title="Activation Sponsorships">
      <Body>Beyond the core levels, these exclusive activations offer high-visibility ownership of signature elements of the event. One sponsor per activation — combinable with any level.</Body>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
        {ACTIVATION_DEFAULTS.map((a) => (
          <div key={a.key} style={{ border: "1px solid #e2e6ee", padding: 12, background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: NAVY, fontWeight: 700, fontSize: 13 }}><Star size={14} style={{ color: RED }} /> {a.label}</div>
              <div className="serif" style={{ fontSize: 16, fontWeight: 700, color: RED, whiteSpace: "nowrap" }}>${a.amount.toLocaleString()}</div>
            </div>
            <div style={{ color: "#57607a", fontSize: 11, marginTop: 3 }}>{a.desc}</div>
          </div>
        ))}
      </div>
      <Callout icon={Handshake} title="What Every Activation Sponsor Receives">{ACTIVATION_INCLUDES} {IN_KIND_NOTE} Activations are limited and secured on a first-commitment basis.</Callout>
    </ContentPage>
  ),

  // 19. MARKETING TIMELINE + DIGITAL REACH
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Exposure Over Time" title="Marketing Exposure Timeline & Digital Reach">
      <div style={{ marginTop: 8 }}>
        {TIMELINE.map(([t, d], i) => (
          <div key={t} style={{ display: "flex", gap: 14, padding: "11px 0", borderBottom: "1px solid #eef1f6" }}>
            <div style={{ width: 30, height: 30, borderRadius: 999, background: i === 3 ? RED : NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
            <div><div style={{ fontWeight: 700, color: INK, fontSize: 13 }}>{t}</div><div style={{ color: "#57607a", fontSize: 12 }}>{d}</div></div>
          </div>
        ))}
      </div>
      <H2 style={{ fontSize: 18, marginTop: 20 }}>Key Sponsor Dates</H2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        <div style={{ border: `2px solid ${RED}`, padding: 14, background: "#fff" }}>
          <div style={{ color: RED, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700 }}>Founding Partner Deadline</div>
          <div className="serif" style={{ fontSize: 20, fontWeight: 700, color: INK, marginTop: 4 }}>{SPONSOR_DEADLINES.foundingDate}</div>
          <div style={{ color: "#57607a", fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>{SPONSOR_DEADLINES.foundingText}</div>
        </div>
        <div style={{ border: "1px solid #e2e6ee", padding: 14, background: "#fff" }}>
          <div style={{ color: NAVY, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700 }}>Commitment Deadline</div>
          <div className="serif" style={{ fontSize: 20, fontWeight: 700, color: INK, marginTop: 4 }}>{SPONSOR_DEADLINES.commitDate}</div>
          <div style={{ color: "#57607a", fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>{SPONSOR_DEADLINES.commitText}</div>
        </div>
      </div>
    </ContentPage>
  ),

  // 20. ON-SITE ACTIVATIONS + HOSPITALITY + NETWORKING
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="On the Course" title="Activations, Hospitality & Networking">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <FeatureCard icon={Megaphone} title="On-Site Activations">Branded tents, product sampling, contests, and interactive experiences at premium levels.</FeatureCard>
        <FeatureCard icon={Award} title="Hospitality Experiences">VIP check-in, on-course refreshments, and reserved reception seating.</FeatureCard>
        <FeatureCard icon={Handshake} title="Networking">Relaxed, relationship-rich access to executives and community leaders.</FeatureCard>
        <FeatureCard icon={Trophy} title="Recognition Moments">Podium acknowledgment and awards-presentation visibility.</FeatureCard>
      </div>
      <Callout icon={Sparkles} title="Silent Auction">A curated silent auction adds energy and an additional avenue for sponsor product placement and brand exposure.</Callout>
    </ContentPage>
  ),

  // 21. EVENT BRANDING + FOUNDING PARTNER PROGRAM
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Ownership & Legacy" title="Event Branding & Founding Partner Program" dark>
      <Body style={{ color: "#c7d0e0" }}>From tee signage and banners to digital features and printed materials, your brand is woven throughout the event experience. Premium partners receive the most prominent, high-traffic placements.</Body>
      <div style={{ border: `2px solid ${GOLD}`, background: NAVY2, padding: 22, marginTop: 20, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 16, right: -34, transform: "rotate(45deg)", background: RED, color: "#fff", fontSize: 10, letterSpacing: "0.15em", padding: "4px 44px", fontWeight: 700 }}>FOUNDING</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: GOLD }}><Award size={20} /><span style={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700 }}>Recognition Program</span></div>
        <div className="serif" style={{ color: "#fff", fontSize: 24, fontWeight: 700, marginTop: 10 }}>Become a Founding Partner</div>
        <div style={{ color: "#dde4f0", fontSize: 12.5, lineHeight: 1.6, marginTop: 8 }}>The inaugural sponsors will forever be recognized as the companies that launched this tradition. As the event grows each year, your organization remains part of its story — permanently featured in Founding Partner recognition. <strong style={{ color: GOLD }}>{FOUNDING_PARTNER.condition}</strong></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
          {FOUNDING_PARTNER.benefits.map((b) => (
            <div key={b.title} style={{ border: `1px solid ${GOLD}`, background: NAVY, padding: "10px 12px" }}>
              <div style={{ color: GOLD, fontWeight: 700, fontSize: 12 }}>{b.title}</div>
              <div style={{ color: "#dde4f0", fontSize: 11, marginTop: 3, lineHeight: 1.5 }}>{b.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </ContentPage>
  ),

  // 22. ROI
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="The Bottom Line" title="Return on Sponsorship Investment">
      <Body>Your sponsorship is an investment with returns that extend well beyond event day — measured in brand equity, relationships, employee pride, and community goodwill.</Body>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
        <FeatureCard icon={Megaphone} title="Brand Equity">Sustained, multi-channel exposure to an engaged, affluent audience.</FeatureCard>
        <FeatureCard icon={Handshake} title="Business Development">Face-to-face access to decision-makers and future clients.</FeatureCard>
        <FeatureCard icon={Heart} title="Corporate Citizenship">Authentic proof of your commitment to service and community.</FeatureCard>
        <FeatureCard icon={Landmark} title="Legacy Value">Permanent Founding Partner recognition in a growing institution.</FeatureCard>
      </div>
      <Callout icon={TrendingUp} title="A Balanced Return">Meaningful community investment + measurable business opportunity = a partnership that pays dividends for years.</Callout>
    </ContentPage>
  ),

  // 23. FAQ
  ({ pageNum, total }) => (
    <ContentPage n={pageNum} total={total} kicker="Good to Know" title="Frequently Asked Questions">
      <div style={{ marginTop: 8 }}>
        {FAQ.map(([q, a]) => (
          <div key={q} style={{ padding: "12px 0", borderBottom: "1px solid #eef1f6" }}>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: 13.5 }}>{q}</div>
            <div style={{ color: "#57607a", fontSize: 12.5, marginTop: 4, lineHeight: 1.6 }}>{a}</div>
          </div>
        ))}
      </div>
    </ContentPage>
  ),

  // 24. CONTACT + COMMITMENT FORM
  ({ pageNum, total }) => (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ background: NAVY, color: "#fff", padding: "40px 56px 30px" }}>
        <Kicker>Let's Partner</Kicker>
        <h2 className="serif" style={{ fontSize: 30, fontWeight: 700, marginTop: 8 }}>Contact & Commitment</h2>
        <div style={{ display: "flex", gap: 26, marginTop: 16, fontSize: 12.5, color: "#e6ebf3", flexWrap: "wrap" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Mail size={14} style={{ color: GOLD }} /> {CONTACT.sponsorEmail}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Phone size={14} style={{ color: GOLD }} /> {CONTACT.sponsorPhone}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Globe size={14} style={{ color: GOLD }} /> {CONTACT.website}</span>
        </div>
      </div>
      <div style={{ padding: "26px 56px", flex: 1 }}>
        <div className="serif" style={{ fontSize: 20, fontWeight: 700, color: INK }}>Sponsorship Commitment Form</div>
        <div style={{ marginTop: 16 }}>
          {[["Company / Organization", "Contact Name"], ["Title", "Email"], ["Phone", "Website"]].map((pair) => (
            <div key={pair.join("-")} style={{ display: "flex", gap: 20, marginBottom: 18 }}>
              {pair.map((f) => (
                <div key={f} style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#8892a5" }}>{f}</div>
                  <div style={{ borderBottom: "1px solid #b8c0cf", height: 22 }} />
                </div>
              ))}
            </div>
          ))}
          <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#8892a5", marginTop: 4 }}>Selected Sponsorship Level</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 16px", marginTop: 8 }}>
            {[...TIERS.map((t) => `${t.name} — $${t.price.toLocaleString()}`), "Sponsor a Hero Team — $2,500"].map((label) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: INK }}>
                <span style={{ width: 13, height: 13, border: "1.5px solid #8892a5", display: "inline-block" }} /> {label}
              </div>
            ))}
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: INK, flexBasis: "100%" }}>
              <span style={{ width: 13, height: 13, border: "1.5px solid #8892a5", display: "inline-block" }} /> Activation Sponsorship:
              <span style={{ borderBottom: "1px solid #b8c0cf", display: "inline-block", width: 220, height: 14 }} />
              <span style={{ color: "#8892a5", fontSize: 10 }}>(e.g., Golf Cart, Beverage Cart, Lunch)</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 30, marginTop: 34 }}>
            <div style={{ flex: 2 }}><div style={{ borderBottom: "1px solid #b8c0cf", height: 26 }} /><div style={{ fontSize: 10, color: "#8892a5", marginTop: 4 }}>Authorized Signature</div></div>
            <div style={{ flex: 1 }}><div style={{ borderBottom: "1px solid #b8c0cf", height: 26 }} /><div style={{ fontSize: 10, color: "#8892a5", marginTop: 4 }}>Date</div></div>
          </div>
        </div>
        <div style={{ marginTop: 26, background: MIST, borderLeft: `4px solid ${RED}`, padding: "12px 16px", fontSize: 11.5, color: "#3c4457" }}>
          Return this form to <strong>{CONTACT.sponsorEmail}</strong> or complete your commitment online at <strong>{CONTACT.website}/sponsor</strong>. Thank you for standing with those who serve.
        </div>
      </div>
      <Footer n={pageNum} total={total} />
    </div>
  ),
];
